import React from "react";
import clsx from "clsx";
import { listSites } from "../api/sites";
import {
  checkAvailabilityRange,
  fetchAvailabilityCalendarMonth,
  fetchAvailabilitySummary,
} from "../api/inverterAvailability";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader } from "../ui/card";
import { DEFAULT_TIMEZONE } from "../constants/timezones";

const weekdays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const isoFromParts = (year, month, day) =>
  [year, String(month + 1).padStart(2, "0"), String(day).padStart(2, "0")].join("-");

const monthKeyFromDate = (value) =>
  `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}`;

const monthKeyFromIso = (iso) => (iso ? iso.slice(0, 7) : "");

const dateFromIso = (iso) => {
  if (!iso) return new Date();
  const [year, month, day] = iso.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const isIsoWithinRange = (iso, start, end) => {
  if (!start || !end) return false;
  return iso >= start && iso <= end;
};

const scriptCache = {};
const loadExternalScript = (src) => {
  if (scriptCache[src]) return scriptCache[src];
  scriptCache[src] = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing && existing.dataset.loaded) {
      resolve();
      return;
    }
    const script = existing || document.createElement("script");
    script.src = src;
    script.async = true;
    script.dataset.loaded = "false";
    script.onload = () => {
      script.dataset.loaded = "true";
      resolve();
    };
    script.onerror = reject;
    if (!existing) {
      document.body.appendChild(script);
    }
  });
  return scriptCache[src];
};

function DateCalendar({
  days = [],
  mode,
  selectedDate,
  rangeStart,
  rangeEnd,
  onSelectDate,
  onSelectRangeDate,
  currentMonth,
  onMonthChange,
  loading,
}) {
  const dayMap = React.useMemo(() => {
    const map = new Map();
    (days || []).forEach((entry) => map.set(entry.day, entry));
    return map;
  }, [days]);

  const monthLabel = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(currentMonth);
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const leadingEmpty = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((leadingEmpty + daysInMonth) / 7) * 7;

  const cells = [];
  for (let i = 0; i < totalCells; i += 1) {
    const dayNumber = i - leadingEmpty + 1;
    if (dayNumber < 1 || dayNumber > daysInMonth) {
      cells.push(null);
    } else {
      cells.push({ day: dayNumber, iso: isoFromParts(year, month, dayNumber) });
    }
  }

  const hasAnyNormalizedDays = (days || []).some((entry) => entry.has_normalized_scada);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => onMonthChange(-1)}
          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400"
        >
          Prev
        </button>
        <div className="text-sm font-semibold text-slate-700">{monthLabel}</div>
        <button
          type="button"
          onClick={() => onMonthChange(1)}
          className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-slate-400"
        >
          Next
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-xs font-semibold text-slate-400">
        {weekdays.map((day) => (
          <div key={day} className="py-1">
            {day}
          </div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((cell, idx) => {
          if (!cell) {
            return <div key={idx} className="h-12 rounded-xl bg-transparent" />;
          }

          const dayState = dayMap.get(cell.iso);
          const hasNormalized = Boolean(dayState?.has_normalized_scada);
          const hasAvailability = Boolean(dayState?.has_availability_rows);
          const isSelectable = hasNormalized && !loading;
          const isSelected = mode === "day" && selectedDate === cell.iso;
          const isRangeStart = mode === "range" && rangeStart === cell.iso;
          const isRangeEnd = mode === "range" && rangeEnd === cell.iso;
          const isInRange =
            mode === "range" &&
            rangeStart &&
            rangeEnd &&
            isIsoWithinRange(cell.iso, rangeStart, rangeEnd);

          const stateClasses =
            isSelected || isRangeStart || isRangeEnd
              ? "border-blue-400 bg-blue-50 text-blue-700"
              : isInRange
                ? "border-blue-200 bg-blue-50/60 text-blue-700"
                : hasAvailability
                  ? "border-slate-200 text-slate-900 hover:border-blue-200 hover:text-blue-700"
                  : hasNormalized
                    ? "border-amber-200 bg-amber-50/60 text-slate-800 hover:border-amber-300"
                    : "border-dashed border-slate-100 text-slate-300";

          let indicatorClass = "";
          if (hasAvailability) {
            indicatorClass = isSelected || isRangeStart || isRangeEnd ? "bg-blue-500" : "bg-emerald-400";
          } else if (hasNormalized) {
            indicatorClass = isSelected || isRangeStart || isRangeEnd ? "bg-blue-300" : "bg-amber-400";
          }

          return (
            <button
              type="button"
              key={cell.iso}
              disabled={!isSelectable}
              onClick={() => {
                if (!isSelectable) return;
                if (mode === "day") {
                  onSelectDate(cell.iso);
                } else {
                  onSelectRangeDate(cell.iso);
                }
              }}
              className={clsx(
                "flex h-12 flex-col items-center justify-center rounded-xl border text-sm transition",
                stateClasses,
                !isSelectable && "cursor-not-allowed"
              )}
            >
              <span className="text-base font-semibold">{cell.day}</span>
              {indicatorClass ? <span className={clsx("mt-1 h-1.5 w-1.5 rounded-full", indicatorClass)} /> : null}
            </button>
          );
        })}
      </div>

      {!loading && (
        <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-400" />
            <span>Stored availability available</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-amber-400" />
            <span>Normalized SCADA only</span>
          </div>
        </div>
      )}

      {!hasAnyNormalizedDays && !loading && (
        <div className="mt-3 rounded-xl border border-amber-100 bg-amber-50 p-3 text-xs text-amber-700">
          No normalized SCADA samples are tracked for this site in the selected month.
        </div>
      )}
      {loading && (
        <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          Loading calendar data...
        </div>
      )}
    </div>
  );
}

const formatWithTimezone = (iso, options, timezone) => {
  if (!iso) return "—";
  const opts = { ...options };
  if (timezone) {
    opts.timeZone = timezone;
  }
  try {
    return new Intl.DateTimeFormat(undefined, opts).format(new Date(iso));
  } catch (err) {
    return new Date(iso).toLocaleString([], options);
  }
};

const formatTime = (iso, timezone) =>
  formatWithTimezone(iso, { hour: "2-digit", minute: "2-digit" }, timezone);

const formatDate = (iso, timezone) =>
  formatWithTimezone(iso, { year: "numeric", month: "2-digit", day: "2-digit" }, timezone);

const formatNumber = (value, digits = 2) => {
  if (value === null || typeof value === "undefined") {
    return "—";
  }
  return Number(value).toFixed(digits);
};

const formatPdfNumber = (value, digits = 2) => {
  if (value === null || typeof value === "undefined") {
    return "—";
  }
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return "—";
  }
  return numberValue.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
};

const formatPdfInteger = (value) => {
  if (value === null || typeof value === "undefined") {
    return "—";
  }
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) {
    return "—";
  }
  return numberValue.toLocaleString(undefined, {
    maximumFractionDigits: 0,
  });
};

const formatExcludedMinutes = (row) => {
  const minutes = Number(row?.low_setpoint_excluded_minutes || 0);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return row?.low_setpoint_excluded ? "excluded" : "";
  }
  return `${Math.round(minutes)} min excluded`;
};

const buildAvailabilityChartScale = (rows) => {
  const values = rows
    .map((row) => Number(row.availability_pct))
    .filter((value) => Number.isFinite(value));

  if (!values.length) {
    return { min: 0, max: 100, ticks: [0, 25, 50, 75, 100] };
  }

  const rawMin = Math.min(...values);
  const spreadToCeiling = 100 - rawMin;
  const padding = spreadToCeiling < 1 ? 0.5 : Math.max(0.3, spreadToCeiling * 0.2);
  const min = Math.max(0, Math.floor((rawMin - padding) * 10) / 10);
  const max = 100;
  const range = max - min || 1;
  const ticks = Array.from({ length: 5 }, (_, idx) =>
    Number((min + (range * idx) / 4).toFixed(1))
  );
  ticks[0] = min;
  ticks[ticks.length - 1] = max;
  return { min, max, ticks };
};

export default function InverterAvailability() {
  const [sites, setSites] = React.useState([]);
  const [selectedSite, setSelectedSite] = React.useState("");
  const [calendarMonths, setCalendarMonths] = React.useState({});
  const [currentMonth, setCurrentMonth] = React.useState(() => new Date());
  const [selectedDate, setSelectedDate] = React.useState("");
  const [rangeStart, setRangeStart] = React.useState("");
  const [rangeEnd, setRangeEnd] = React.useState("");
  const [reportMode, setReportMode] = React.useState("day");
  const [calendarLoading, setCalendarLoading] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [checking, setChecking] = React.useState(false);
  const [tableData, setTableData] = React.useState(null);
  const [error, setError] = React.useState("");
  const [statusInfo, setStatusInfo] = React.useState(null);
  const [printing, setPrinting] = React.useState(false);
  const [selectionInitialized, setSelectionInitialized] = React.useState(false);

  const currentMonthKey = React.useMemo(() => monthKeyFromDate(currentMonth), [currentMonth]);
  const currentMonthData = calendarMonths[currentMonthKey] || null;
  const currentMonthDays = currentMonthData?.days || [];

  React.useEffect(() => {
    let ignore = false;
    const loadSites = async () => {
      try {
        const data = await listSites();
        if (ignore) return;
        setSites(data || []);
        if (data && data.length > 0) {
          setSelectedSite(String(data[0].id));
        }
      } catch (err) {
        if (!ignore) {
          setError("Unable to load sites.");
        }
      }
    };
    loadSites();
    return () => {
      ignore = true;
    };
  }, []);

  React.useEffect(() => {
    setCalendarMonths({});
    setCurrentMonth(new Date());
    setSelectedDate("");
    setRangeStart("");
    setRangeEnd("");
    setTableData(null);
    setStatusInfo(null);
    setError("");
    setSelectionInitialized(false);
  }, [selectedSite]);

  React.useEffect(() => {
    if (!selectedSite) return;
    if (calendarMonths[currentMonthKey]) return;

    let cancelled = false;
    const loadMonth = async () => {
      setCalendarLoading(true);
      try {
        const data = await fetchAvailabilityCalendarMonth({
          siteId: Number(selectedSite),
          month: currentMonthKey,
        });
        if (cancelled) return;
        setCalendarMonths((prev) => ({ ...prev, [currentMonthKey]: data }));

        if (!selectionInitialized) {
          const latestAvailability = data?.latest_day_with_availability_rows || "";
          const latestNormalized = data?.latest_day_with_normalized_scada || "";
          const earliestAvailability = data?.earliest_day_with_availability_rows || "";
          const earliestNormalized = data?.earliest_day_with_normalized_scada || "";
          const focusDay = latestAvailability || latestNormalized;

          if (focusDay && monthKeyFromIso(focusDay) !== currentMonthKey) {
            setCurrentMonth(dateFromIso(focusDay));
            return;
          }

          if (focusDay) {
            setSelectedDate((prev) => prev || focusDay);
          }

          const defaultRangeStart = earliestAvailability || earliestNormalized || "";
          const defaultRangeEnd = latestAvailability || latestNormalized || "";
          if (defaultRangeStart) {
            setRangeStart((prev) => prev || defaultRangeStart);
          }
          if (defaultRangeEnd) {
            setRangeEnd((prev) => prev || defaultRangeEnd);
          }
          setSelectionInitialized(true);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Failed to load availability calendar data for the selected site.");
        }
      } finally {
        if (!cancelled) {
          setCalendarLoading(false);
        }
      }
    };

    loadMonth();
    return () => {
      cancelled = true;
    };
  }, [selectedSite, currentMonthKey, calendarMonths, selectionInitialized]);

  const daySelectionReady = reportMode === "day" && selectedDate;
  const rangeSelectionReady =
    reportMode === "range" &&
    rangeStart &&
    rangeEnd &&
    new Date(rangeStart) <= new Date(rangeEnd);

  const selectionLabel =
    reportMode === "day"
      ? selectedDate || "—"
      : rangeStart && rangeEnd
        ? `${rangeStart} → ${rangeEnd}`
        : "—";

  const selectionContext = React.useMemo(() => {
    const siteId = Number(selectedSite);
    if (!siteId) return null;
    if (reportMode === "day") {
      if (!selectedDate) return null;
      return { siteId, startDate: selectedDate, endDate: selectedDate, grouping: "hour" };
    }
    if (!rangeStart || !rangeEnd || new Date(rangeStart) > new Date(rangeEnd)) {
      return null;
    }
    return { siteId, startDate: rangeStart, endDate: rangeEnd, grouping: "day" };
  }, [selectedSite, reportMode, selectedDate, rangeStart, rangeEnd]);

  React.useEffect(() => {
    setTableData(null);
    setError("");
  }, [selectionContext?.siteId, selectionContext?.startDate, selectionContext?.endDate, reportMode]);

  React.useEffect(() => {
    if (!selectionContext) {
      setStatusInfo(null);
      return;
    }

    let cancelled = false;
    const loadStatus = async () => {
      setStatusInfo(null);
      setChecking(true);
      try {
        const status = await checkAvailabilityRange(selectionContext);
        if (!cancelled) {
          setStatusInfo(status);
        }
      } catch (err) {
        if (!cancelled) {
          setStatusInfo(null);
          setError("Unable to check stored availability for this period.");
        }
      } finally {
        if (!cancelled) {
          setChecking(false);
        }
      }
    };

    loadStatus();
    return () => {
      cancelled = true;
    };
  }, [selectionContext]);

  const runSummary = async (context) => {
    setError("");
    setLoading(true);
    try {
      const summary = await fetchAvailabilitySummary(context);
      setTableData(summary);
    } catch (err) {
      console.error(err);
      setTableData(null);
      const detail = err?.response?.data?.detail;
      setError(
        detail?.message ||
          detail ||
          "Stored inverter availability report is missing for one or more selected days. Use Data Maintenance to regenerate availability reports."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectionContext) {
      setError("Please select a valid reporting period.");
      return;
    }
    if (!statusInfo?.has_data) {
      setError(
        "Stored inverter availability report is missing for one or more selected days. Use Data Maintenance to regenerate availability reports."
      );
      return;
    }
    await runSummary(selectionContext);
  };

  const handleSelectDay = React.useCallback((iso) => {
    setSelectedDate(iso);
    setTableData(null);
    setStatusInfo(null);
    setError("");
  }, []);

  const handleSelectRangeDate = React.useCallback(
    (iso) => {
      setTableData(null);
      setStatusInfo(null);
      setError("");

      if (!rangeStart || (rangeStart && rangeEnd)) {
        setRangeStart(iso);
        setRangeEnd("");
        return;
      }

      if (iso < rangeStart) {
        setRangeStart(iso);
        return;
      }

      setRangeEnd(iso);
    },
    [rangeStart, rangeEnd]
  );

  const summaryRows = React.useMemo(() => {
    if (!tableData?.rows) return [];
    return [...tableData.rows].sort((a, b) => new Date(a.start) - new Date(b.start));
  }, [tableData]);
  const availabilityAverage = tableData?.availability_avg_pct;
  const totalInverters = tableData?.total_inverters ?? 0;
  const reportNotes = Array.isArray(tableData?.notes) ? tableData.notes : [];
  const lowSetpointThreshold =
    typeof tableData?.low_setpoint_threshold_pct === "number" ? tableData.low_setpoint_threshold_pct : 2.0;
  const selectedSiteObj = React.useMemo(
    () => sites.find((s) => String(s.id) === String(selectedSite)) || null,
    [sites, selectedSite]
  );
  const reportTimezone = tableData?.timezone || currentMonthData?.timezone || selectedSiteObj?.timezone || DEFAULT_TIMEZONE;

  const ensurePrintLibs = React.useCallback(async () => {
    if (!window.jspdf || !window.jspdf.jsPDF) {
      await loadExternalScript("https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js");
    }
    if (!window.jspdf?.jsPDF?.API?.autoTable) {
      await loadExternalScript(
        "https://cdn.jsdelivr.net/npm/jspdf-autotable@3.8.4/dist/jspdf.plugin.autotable.min.js"
      );
    }
    if (!window.Chart) {
      await loadExternalScript("https://cdn.jsdelivr.net/npm/chart.js@4.4.7/dist/chart.umd.min.js");
    }
  }, []);

  const handlePrintReport = React.useCallback(async () => {
    if (!tableData || !summaryRows.length) {
      setError("Please generate a report before printing.");
      return;
    }
    setPrinting(true);
    try {
      await ensurePrintLibs();
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF("portrait", "pt", "a4");
      const site = selectedSiteObj || {};
      const generatedAtDate = new Date();
      const generatedDateLabel = generatedAtDate.toLocaleDateString();
      const generatedTimeLabel = generatedAtDate.toLocaleTimeString();
      const chartRows = summaryRows.filter((row) => typeof row.availability_pct === "number");
      const avgAvailabilityValue =
        typeof tableData.availability_avg_pct === "number" ? Number(tableData.availability_avg_pct) : null;
      const availabilityValues = chartRows
        .map((row) => Number(row.availability_pct))
        .filter((value) => Number.isFinite(value));
      const bestAvailabilityValue = availabilityValues.length ? Math.max(...availabilityValues) : null;
      const worstAvailabilityValue = availabilityValues.length ? Math.min(...availabilityValues) : null;
      const dsoValues = summaryRows
        .map((row) => Number(row.dso_setpoint))
        .filter((value) => Number.isFinite(value));
      const avgDsoValue = dsoValues.length
        ? dsoValues.reduce((sum, value) => sum + value, 0) / dsoValues.length
        : null;
      const pdfNotes = Array.isArray(tableData?.notes) ? tableData.notes : [];

      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("Inverter Availability Report", 40, 50);

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageMargin = 40;
      const slate = [71, 85, 105];
      const ink = [33, 37, 41];
      const blue = [21, 101, 192];
      const blueSoft = [248, 250, 252];
      const blueBorder = [220, 226, 239];
      const emerald = [22, 163, 74];
      const amber = [245, 158, 11];
      const orange = [249, 115, 22];

      const headerBar = (title, rightText, y) => {
        doc.setFillColor(...blue);
        doc.rect(pageMargin, y, pageWidth - pageMargin * 2, 24, "F");
        doc.setTextColor(255);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.text(title, pageMargin + 10, y + 16);
        if (rightText) {
          doc.text(rightText, pageWidth - pageMargin - 10, y + 16, { align: "right" });
        }
        doc.setTextColor(...ink);
        return y + 24;
      };

      const drawCardAt = (x, y, width, rows, forcedHeight) => {
        const pad = 10;
        const lineH = 12;
        const height = forcedHeight ?? pad * 2 + rows.length * lineH;
        doc.setDrawColor(...blueBorder);
        doc.setFillColor(...blueSoft);
        doc.rect(x, y, width, height, "FD");
        doc.setFontSize(9.5);
        rows.forEach((row, idx) => {
          const baseY = y + pad + lineH * (idx + 0.65);
          doc.setTextColor(...slate);
          doc.setFont("helvetica", "bold");
          doc.text(`${row.label}:`, x + pad, baseY);
          doc.setFont("helvetica", "normal");
          if (row.color) {
            doc.setTextColor(...row.color);
          } else {
            doc.setTextColor(...ink);
          }
          doc.text(row.value || "—", x + width - pad, baseY, { align: "right" });
        });
        doc.setTextColor(...ink);
        return y + height;
      };

      const drawPdfNotesAt = (startY) => {
        if (!pdfNotes.length) {
          return startY;
        }
        let notesY = startY;
        const estimatedHeight = 34 + pdfNotes.length * 18;
        if (notesY + estimatedHeight > pageHeight - 44) {
          doc.addPage();
          notesY = 54;
        }
        notesY = headerBar("REPORT NOTES", "", notesY) + 6;
        doc.autoTable({
          startY: notesY,
          margin: { left: pageMargin, right: pageMargin },
          body: pdfNotes.map((note) => [note]),
          styles: { font: "helvetica", fontSize: 8.5, cellPadding: 4, textColor: [120, 53, 15] },
          bodyStyles: { fillColor: [255, 251, 235] },
          theme: "plain",
        });
        return (doc.lastAutoTable?.finalY || notesY) + 10;
      };

      let y = 76;
      y = headerBar("KEY PERFORMANCE INDICATORS", `Report date: ${formatDate(tableData.start, reportTimezone)}`, y) + 8;

      const colGap = 12;
      const colWidth = (pageWidth - pageMargin * 2 - colGap) / 2;
      const leftRows = [
        { label: "Site", value: site.name || "—" },
        { label: "Location", value: [site.city, site.country].filter(Boolean).join(", ") || "—" },
        { label: "Address", value: [site.zip_code, site.address].filter(Boolean).join(" ") || "—" },
        { label: "Time zone", value: reportTimezone },
        {
          label: "Coordinates",
          value:
            site.gps_latitude != null && site.gps_longitude != null
              ? `${site.gps_latitude}, ${site.gps_longitude}`
              : "—",
        },
        { label: "AC capacity (MVA)", value: site.ac_nominal_capacity != null ? formatPdfNumber(site.ac_nominal_capacity, 3) : "—" },
        { label: "DC capacity (MWp)", value: site.dc_nominal_capacity != null ? formatPdfNumber(site.dc_nominal_capacity, 3) : "—" },
      ];
      const rightRows = [
        {
          label: "Average availability",
          value: avgAvailabilityValue != null ? `${formatPdfNumber(avgAvailabilityValue, 2)}%` : "—",
          color: avgAvailabilityValue != null ? emerald : undefined,
        },
        { label: "Installed inverters", value: formatPdfInteger(totalInverters) },
        { label: "Selected period", value: reportMode === "day" ? "Daily" : "Multi-day" },
        { label: "Periods shown", value: formatPdfInteger(summaryRows.length) },
        {
          label: "DSO setpoint avg",
          value: avgDsoValue != null ? `${formatPdfNumber(avgDsoValue, 2)}%` : "—",
        },
        {
          label: "Irradiation threshold",
          value: site.irrad_avail != null ? `${formatPdfNumber(site.irrad_avail, 0)} W/m²` : "—",
        },
      ];

      const mainCardHeight = Math.max(20 + leftRows.length * 12, 20 + rightRows.length * 12);
      const leftEndY = drawCardAt(pageMargin, y, colWidth, leftRows, mainCardHeight);
      const rightEndY = drawCardAt(pageMargin + colWidth + colGap, y, colWidth, rightRows, mainCardHeight);
      y = Math.max(leftEndY, rightEndY) + 10;

      const miniGap = 12;
      const miniWidth = (pageWidth - pageMargin * 2 - miniGap * 2) / 3;
      const miniRows = [
        [
          { label: "Generated", value: generatedDateLabel },
          { label: "Generated time", value: generatedTimeLabel },
          { label: "Timezone", value: reportTimezone },
        ],
        [
          { label: "Start date", value: formatDate(tableData.start, reportTimezone) },
          { label: "End date", value: formatDate(tableData.end, reportTimezone) },
        ],
        [
          {
            label: "Best availability",
            value: bestAvailabilityValue != null ? `${formatPdfNumber(bestAvailabilityValue, 2)}%` : "—",
            color: bestAvailabilityValue != null ? emerald : undefined,
          },
          {
            label: "Worst availability",
            value: worstAvailabilityValue != null ? `${formatPdfNumber(worstAvailabilityValue, 2)}%` : "—",
            color: worstAvailabilityValue != null ? amber : undefined,
          },
          {
            label: "Average availability",
            value: avgAvailabilityValue != null ? `${formatPdfNumber(avgAvailabilityValue, 2)}%` : "—",
            color: avgAvailabilityValue != null ? emerald : undefined,
          },
        ],
      ];

      const miniCardHeight = 20 + 3 * 12;
      miniRows.forEach((rows, idx) => {
        drawCardAt(pageMargin + idx * (miniWidth + miniGap), y, miniWidth, rows, miniCardHeight);
      });
      y += miniCardHeight + 8;

      const tableBody = summaryRows.map((row) => [
        reportMode === "day"
          ? formatTime(row.start, reportTimezone)
          : formatDate(row.start, reportTimezone),
        reportMode === "day" ? formatTime(row.end, reportTimezone) : formatDate(row.end, reportTimezone),
        formatPdfInteger(row.valid_points ?? 0),
        typeof row.valid_points_per_inverter === "number" ? formatPdfNumber(row.valid_points_per_inverter, 2) : "—",
        [
          typeof row.dso_setpoint === "number" ? formatPdfNumber(row.dso_setpoint, 2) : "—",
          formatExcludedMinutes(row),
        ]
          .filter(Boolean)
          .join(" "),
        formatPdfInteger(totalInverters),
        typeof row.available_inverters === "number" ? formatPdfNumber(row.available_inverters, 2) : "—",
        typeof row.availability_pct === "number" ? formatPdfNumber(row.availability_pct, 2) : "—",
      ]);

      const tableFontSize = reportMode === "day" ? 8.3 : 9;
      const tableCellPadding = reportMode === "day" ? 3.2 : 4;

      doc.autoTable({
        startY: y,
        margin: { left: pageMargin, right: pageMargin },
        head: [
          ["Start", "End", "Valid points", "Valid / Inv", "DSO setpoint (%)", "Installed", "Available", "Availability (%)"],
        ],
        body: tableBody,
        foot:
          typeof tableData.availability_avg_pct === "number"
            ? [
                [
                  { content: "Average availability", colSpan: 7, styles: { halign: "right" } },
                  `${formatPdfNumber(tableData.availability_avg_pct, 2)}%`,
                ],
              ]
            : [],
        styles: { font: "helvetica", fontSize: tableFontSize, cellPadding: tableCellPadding, halign: "right" },
        headStyles: { fillColor: [37, 99, 235], textColor: 255 },
        footStyles: { fillColor: [248, 250, 252], textColor: 30, fontStyle: "bold" },
        columnStyles: {
          0: { halign: "center" },
          1: { halign: "center" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "right" },
          7: { halign: "right" },
        },
      });

      if (chartRows.length) {
        doc.addPage();

        const chartPageWidth = doc.internal.pageSize.getWidth();
        const chartPageHeight = doc.internal.pageSize.getHeight();
        const chartMargin = 44;
        const chartBoxX = chartMargin;
        const chartBoxY = 90;
        const chartBoxWidth = chartPageWidth - chartMargin * 2;
        const chartBoxHeight = Math.min(360, chartPageHeight - 180);
        const plotLeft = chartBoxX + 52;
        const plotRight = chartBoxX + chartBoxWidth - 20;
        const plotTop = chartBoxY + 36;
        const plotBottom = chartBoxY + chartBoxHeight - 48;
        const span = chartRows.length > 1 ? chartRows.length - 1 : 1;
        const yScale = buildAvailabilityChartScale(chartRows);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(16);
        doc.text("Availability trend", chartBoxX, 54);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(
          reportMode === "day"
            ? "Hourly stored availability for the selected day"
            : "Daily stored availability for the selected reporting window",
          chartBoxX,
          72
        );

        doc.setDrawColor(210);
        doc.roundedRect(chartBoxX, chartBoxY, chartBoxWidth, chartBoxHeight, 10, 10);

        doc.setFontSize(9);
        doc.setTextColor(90);
        doc.text("Availability (%)", plotLeft, plotTop - 14);
        yScale.ticks.forEach((tick) => {
          const ratio = yScale.max === yScale.min ? 0 : (tick - yScale.min) / (yScale.max - yScale.min);
          const y = plotBottom - ratio * (plotBottom - plotTop);
          doc.setDrawColor(232);
          doc.line(plotLeft, y, plotRight, y);
          doc.text(formatPdfNumber(tick, 1), plotLeft - 38, y + 3);
        });

        doc.setDrawColor(203, 213, 225);
        doc.line(plotLeft, plotBottom, plotRight, plotBottom);

        if (avgAvailabilityValue != null) {
          const avgRatio =
            yScale.max === yScale.min ? 0.5 : (avgAvailabilityValue - yScale.min) / (yScale.max - yScale.min);
          const avgY = plotBottom - avgRatio * (plotBottom - plotTop);
          doc.setDrawColor(...orange);
          doc.setLineWidth(1.2);
          if (typeof doc.setLineDashPattern === "function") {
            doc.setLineDashPattern([5, 4], 0);
          }
          doc.line(plotLeft, avgY, plotRight, avgY);
          if (typeof doc.setLineDashPattern === "function") {
            doc.setLineDashPattern([], 0);
          }
        }

        doc.setDrawColor(...blue);
        doc.setFillColor(...blue);
        doc.setLineWidth(1.8);
        chartRows.forEach((row, idx) => {
          const x = plotLeft + ((plotRight - plotLeft) * idx) / span;
          const pct = Number(row.availability_pct ?? 0);
          const ratio = yScale.max === yScale.min ? 0.5 : (pct - yScale.min) / (yScale.max - yScale.min);
          const y = plotBottom - ratio * (plotBottom - plotTop);
          if (idx === 0) {
            doc.moveTo(x, y);
          } else {
            doc.lineTo(x, y);
          }
        });
        doc.stroke();

        const labelCount = Math.min(6, chartRows.length);
        const labelIndexes = new Set(
          Array.from({ length: labelCount }, (_, idx) =>
            Math.round((idx * (chartRows.length - 1)) / Math.max(1, labelCount - 1))
          )
        );

        chartRows.forEach((row, idx) => {
          const x = plotLeft + ((plotRight - plotLeft) * idx) / span;
          const pct = Number(row.availability_pct ?? 0);
          const ratio = yScale.max === yScale.min ? 0.5 : (pct - yScale.min) / (yScale.max - yScale.min);
          const y = plotBottom - ratio * (plotBottom - plotTop);
          doc.circle(x, y, 2.6, "F");

          if (labelIndexes.has(idx)) {
            const label =
              reportMode === "day"
                ? formatTime(row.start, reportTimezone)
                : formatDate(row.start, reportTimezone);
            doc.setFontSize(8);
            doc.setTextColor(100);
            doc.text(label, x, plotBottom + 16, { align: "center" });
          }
        });

        const avgAvailability =
          typeof tableData.availability_avg_pct === "number"
            ? `${formatPdfNumber(tableData.availability_avg_pct, 2)}%`
            : "—";
        const legendY = chartBoxY + chartBoxHeight + 22;
        doc.setFontSize(10);
        if (avgAvailabilityValue != null) {
          doc.setDrawColor(...orange);
          if (typeof doc.setLineDashPattern === "function") {
            doc.setLineDashPattern([5, 4], 0);
          }
          doc.line(chartBoxX, legendY - 3, chartBoxX + 26, legendY - 3);
          if (typeof doc.setLineDashPattern === "function") {
            doc.setLineDashPattern([], 0);
          }
          doc.setTextColor(...orange);
          doc.text(`Average availability reference (${avgAvailability})`, chartBoxX + 34, legendY);
        }
        doc.setTextColor(70);
        doc.text(`Site: ${site.name || "—"}`, chartBoxX, legendY + 18);
        doc.setTextColor(...ink);

        drawPdfNotesAt(legendY + 42);
      } else if (pdfNotes.length) {
        drawPdfNotesAt((doc.lastAutoTable?.finalY || y) + 20);
      }

      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i += 1) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text("created with ", 40, pageHeight - 20);
        doc.setFont("helvetica", "bold");
        doc.text("PV-report.com", 98, pageHeight - 20);
        doc.setFont("helvetica", "normal");
        doc.text(`page ${i}/${pageCount}`, pageWidth / 2 - 20, pageHeight - 20);
        doc.text("powered by ", pageWidth - 140, pageHeight - 20);
        doc.setFont("helvetica", "bold");
        doc.text("T-NRG", pageWidth - 70, pageHeight - 20);
      }

      const pdfUrl = doc.output("bloburl");
      window.open(pdfUrl, "_blank");
    } catch (err) {
      console.error("Failed to generate PDF", err);
      setError("Unable to generate the PDF report.");
    } finally {
      setPrinting(false);
    }
  }, [
    tableData,
    summaryRows,
    ensurePrintLibs,
    reportMode,
    selectedSiteObj,
    totalInverters,
    setError,
    reportTimezone,
  ]);

  const generateButtonLabel = checking ? "Checking..." : loading ? "Loading..." : "Generate report";
  const generateDisabled =
    loading ||
    checking ||
    !selectedSite ||
    (reportMode === "day" ? !daySelectionReady : !rangeSelectionReady) ||
    !statusInfo?.has_data;

  const missingDaysPreview = statusInfo?.missing_availability_days?.slice(0, 3) || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Inverter Availability</h1>
          <p className="text-slate-600">
            Display per-inverter availability from stored KPI data. Use Data Maintenance to calculate or refresh
            availability before opening a report window.
          </p>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            className="rounded-md border px-3 py-2 text-sm"
            value={selectedSite || ""}
            onChange={(e) => setSelectedSite(e.target.value)}
          >
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
          <div className="inline-flex overflow-hidden rounded-md border text-sm font-medium text-slate-600">
            <button
              type="button"
              className={clsx(
                "px-3 py-2 transition",
                reportMode === "day" ? "bg-blue-600 text-white" : "bg-white hover:bg-slate-50"
              )}
              onClick={() => setReportMode("day")}
            >
              Daily
            </button>
            <button
              type="button"
              className={clsx(
                "px-3 py-2 transition",
                reportMode === "range" ? "bg-blue-600 text-white" : "bg-white hover:bg-slate-50"
              )}
              onClick={() => setReportMode("range")}
            >
              Multi-day
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <div className="font-semibold text-slate-800">
              {reportMode === "day" ? "Select Day" : "Select Period"}
            </div>
            <p className="text-sm text-slate-500">
              Green dots already have stored availability. Amber dots only have normalized SCADA data.
            </p>
          </CardHeader>
          <CardContent>
            <DateCalendar
              days={currentMonthDays}
              mode={reportMode}
              selectedDate={selectedDate}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onSelectDate={handleSelectDay}
              onSelectRangeDate={handleSelectRangeDate}
              currentMonth={currentMonth}
              onMonthChange={(delta) => {
                const next = new Date(currentMonth);
                next.setMonth(next.getMonth() + delta);
                setCurrentMonth(next);
              }}
              loading={calendarLoading}
            />
            {reportMode === "range" && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                {rangeStart && !rangeEnd
                  ? `Range start selected: ${rangeStart}. Pick an end date from this or another month.`
                  : rangeStart && rangeEnd
                    ? `Selected range: ${rangeStart} → ${rangeEnd}`
                    : "Pick a start date, then pick an end date."}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="font-semibold text-slate-800">Report actions</div>
            <p className="text-sm text-slate-500">
              Display stored availability for the selected window. This page does not recalculate KPI rows.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <div className="text-xs uppercase text-slate-500">Site</div>
              <div className="text-base font-semibold text-slate-900">
                {sites.find((s) => String(s.id) === String(selectedSite))?.name || "—"}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase text-slate-500">Selected period</div>
              <div className="text-base font-semibold text-slate-900">{selectionLabel}</div>
            </div>
            {statusInfo && (
              <div
                className={clsx(
                  "rounded-lg border p-3 text-xs",
                  statusInfo.has_data
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-amber-200 bg-amber-50 text-amber-700"
                )}
              >
                {statusInfo.has_data ? (
                  <span>
                    Stored availability is ready for {statusInfo.availability_days} selected day(s) · Total inverters:{" "}
                    {statusInfo.total_inverters}
                  </span>
                ) : (
                  <span>
                    Stored availability is missing for {statusInfo.missing_availability_days.length} of{" "}
                    {statusInfo.selected_days} selected day(s). Use Data Maintenance to regenerate availability reports.
                    {missingDaysPreview.length ? ` Missing example: ${missingDaysPreview.join(", ")}` : ""}
                  </span>
                )}
              </div>
            )}
            {error && <div className="text-sm text-rose-600">{error}</div>}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button onClick={handleGenerate} disabled={generateDisabled}>
                {generateButtonLabel}
              </Button>
              <Button
                variant="outline"
                onClick={handlePrintReport}
                disabled={printing || !summaryRows.length || loading || checking}
              >
                {printing ? "Preparing..." : "Print report"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-1">
            <div className="font-semibold text-slate-800">Availability summary</div>
            <p className="text-sm text-slate-500">
              {tableData
                ? `Data window ${formatDate(tableData.start, reportTimezone)} → ${formatDate(
                    tableData.end,
                    reportTimezone
                  )}`
                : "Select a stored availability window and click Generate report."}
            </p>
            {tableData && <p className="text-xs text-slate-500">Times shown in {reportTimezone}</p>}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {reportNotes.length > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="font-semibold">Availability report note</div>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                {reportNotes.map((note, index) => (
                  <li key={`${index}-${note}`}>{note}</li>
                ))}
              </ul>
            </div>
          )}
          {loading && <div className="py-6 text-center text-sm text-slate-500">Loading stored availability...</div>}
          {!loading && summaryRows.length === 0 && (
            <div className="py-6 text-center text-sm text-slate-500">No stored availability rows for this selection.</div>
          )}
          {!loading && summaryRows.length > 0 && (
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">{reportMode === "day" ? "Start Time" : "Date"}</th>
                  <th className="px-3 py-2 text-left">{reportMode === "day" ? "End Time" : ""}</th>
                  <th className="px-3 py-2 text-right">Valid datapoints</th>
                  <th className="px-3 py-2 text-right">Valid / inverter</th>
                  <th className="px-3 py-2 text-right">DSO setpoint</th>
                  <th className="px-3 py-2 text-right">Installed inverters</th>
                  <th className="px-3 py-2 text-right">Available inverters</th>
                  <th className="px-3 py-2 text-right">Availability</th>
                </tr>
              </thead>
              <tbody>
                {summaryRows.map((row) => {
                  const key = row.start;
                  const startLabel =
                    reportMode === "day"
                      ? formatTime(row.start, reportTimezone)
                      : formatDate(row.start, reportTimezone);
                  const endLabel = reportMode === "day" ? formatTime(row.end, reportTimezone) : "";
                  const excludedMinutesLabel = formatExcludedMinutes(row);
                  return (
                    <tr
                      key={key}
                      className={clsx(
                        "border-b last:border-b-0",
                        row.low_setpoint_excluded && "bg-amber-50/60"
                      )}
                    >
                      <td className="px-3 py-2">{startLabel}</td>
                      <td className="px-3 py-2">{endLabel}</td>
                      <td className="px-3 py-2 text-right">{row.valid_points ?? "—"}</td>
                      <td className="px-3 py-2 text-right">
                        {typeof row.valid_points_per_inverter === "number"
                          ? formatNumber(row.valid_points_per_inverter, 2)
                          : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {typeof row.dso_setpoint === "number" ? `${formatNumber(row.dso_setpoint)}%` : "—"}
                        {row.low_setpoint_excluded ? (
                          <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                            {excludedMinutesLabel || "excluded"}
                          </span>
                        ) : null}
                      </td>
                      <td className="px-3 py-2 text-right">{totalInverters}</td>
                      <td className="px-3 py-2 text-right">
                        {typeof row.available_inverters === "number" ? formatNumber(row.available_inverters, 2) : "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {typeof row.availability_pct === "number" ? `${formatNumber(row.availability_pct)}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-50 font-semibold text-slate-700">
                  <td className="px-3 py-2" colSpan={7}>
                    Average availability
                  </td>
                  <td className="px-3 py-2 text-right">
                    {typeof availabilityAverage === "number" ? `${formatNumber(availabilityAverage)}%` : "—"}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
          <p className="mt-3 text-xs text-slate-400">
            Valid datapoints require irradiance ≥ site threshold and DSO setpoint ≥ {formatNumber(lowSetpointThreshold, 1)}%
            when a setpoint is available. Availability = available inverters / installed inverters. DSO setpoint shows the average command (%).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
